import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { BrowserTabTitle, LoadingOverlay } from '../../../../components/display';
import { FormikControl, checkResponses } from '../../../../components/form';
import { getDataArray } from '../../../../components/functions';
import { Table } from '../../../../components/table';
import { Socket } from '../../../../components/ws';

import { fetchMemberRequestsAll } from '../../../../actions';

const pageTitle = 'Member Requests';

const defaultSorted = { dataField: 'created_at', order: 'desc' };

function MemberRequests(props){
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [memberRequests, setMemberRequests] = useState([]);
    const [filteredMemberRequests, setFilteredMemberRequests] = useState([]);

    const { fetchMemberRequestsAll } = props;

    const filterMemberRequests = useCallback((unfilteredRequests, filterQuery) => {
        if (!filterQuery || filterQuery.trim() === '') {
            setFilteredMemberRequests(unfilteredRequests);
            return;
        }
        const query = filterQuery.toLowerCase().trim();
        const filtered = unfilteredRequests.filter(mr => {
            return (mr.first_name && mr.first_name.toLowerCase().includes(query)) ||
                   (mr.last_name && mr.last_name.toLowerCase().includes(query)) ||
                   (mr.email && mr.email.toLowerCase().includes(query)) ||
                   (mr.phone && mr.phone.toLowerCase().includes(query));
        });
        setFilteredMemberRequests(filtered);
    }, []);

    const refreshData = useCallback(() => {
        (async function refresh(){
            if(loading) return;
            if(mounted.current) setLoading(true);

            try {
                const memberRequestsRes = await fetchMemberRequestsAll();
                const isApiError = checkResponses(memberRequestsRes);
                if(isApiError){
                    if(mounted.current){
                        setApiError('Error fetching data from the server. Please try again later.');
                        setLoading(false);
                        setHasLoaded(true);
                    }
                    return;
                } else setApiError(false);

                const newMemberRequests = getDataArray(memberRequestsRes);
                const { filterQuery } = formRef.current?.values || { filterQuery: '' };

                if(mounted.current){
                    setMemberRequests(newMemberRequests);
                    filterMemberRequests(newMemberRequests, filterQuery);
                    setHasLoaded(true);
                    setLoading(false);
                }
            } catch (error) {
                console.error('[MEMBER REQUESTS] Error:', error);
                if(mounted.current){
                    setApiError('Error fetching data. Please try again later.');
                    setLoading(false);
                    setHasLoaded(true);
                }
            }
        })();
    }, [mounted, loading, fetchMemberRequestsAll, filterMemberRequests]);

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getColumns = () => [
        { dataField: 'first_name', text: 'First Name', sort: true },
        { dataField: 'last_name', text: 'Last Name', sort: true },
        { dataField: 'email', text: 'Email', sort: true },
        { dataField: 'phone', text: 'Phone' },
        { dataField: 'created_at', text: 'Request Date', sort: true },
        {
            dataField: 'status',
            text: 'Status',
            sort: true,
            formatter: (row) => (
                <span className={row.status === 'approved' ? 'text-green-600' : row.status === 'pending' ? 'text-orange-600' : 'text-gray-400'}>
                    {row.status || 'Pending'}
                </span>
            )
        }
    ];

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay/>}
            <div className="card">
                <Formik
                    enableReinitialize
                    initialValues={{ filterQuery: '' }}
                    innerRef={formRef}
                    onSubmit={() => null}
                >
                    {formik => (
                        <form onSubmit={formik.handleSubmit}>
                            <h2>{pageTitle}</h2>
                            <br/>
                            <div className="flex flex-row gap-x-4 items-center mb-4">
                                <div className="grid grid-cols-1 gap-y-2 w-1/3">
                                    <FormikControl
                                        id="member-requests-searchQuery"
                                        name="filterQuery"
                                        placeholder="Search member requests..."
                                        value={formik.values.filterQuery}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterMemberRequests(memberRequests, e.target.value);
                                        }}
                                    />
                                </div>
                            </div>
                            {apiError ? <div className="text-mpLRed">{apiError}</div> :
                                hasLoaded &&
                                <Table
                                    tableName="memberRequests"
                                    data={filteredMemberRequests}
                                    columns={getColumns()}
                                    defaultSorted={defaultSorted}
                                    emptyMessage="No member requests found"
                                />
                            }
                        </form>
                    )}
                </Formik>
            </div>
            <Socket refreshData={refreshData} page={pageTitle} setVersion={props.setVersion} />
        </div>
    );
};

export default connect(null, { fetchMemberRequestsAll })(MemberRequests);


